import axios from "axios";
import qs from "qs";

export interface ApiResponse {
  success: boolean;
  message: string;
  data: string;
}
export interface ApiError {
  data?: {
    success?: boolean;
    error?: {
      message?: string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  status?: number;
  statusText?: string;
  headers?: Record<string, string>;
  config?: Record<string, unknown>;
  request?: XMLHttpRequest;
  error?: {
    message?: string;
    [key: string]: unknown;
  };
  message?: string;
}

const instance = axios.create({
  baseURL: `/api`,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: {
    serialize: (params) =>
      qs.stringify(params, {
        encode: true,
      }),
  },
});

// Improved request interceptor
instance.interceptors.request.use(
  (config) => {
    // Check both localStorage and sessionStorage on every request
    const accessToken =
      localStorage.getItem("auth_token") ||
      sessionStorage.getItem("auth_token");

    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Enhanced response interceptor with automatic token refresh
instance.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors globally
    if (error.response && error.response.status === 401) {
      // Skip refresh if this is already a refresh token request
      if (originalRequest.url?.includes("/user/refresh")) {
        // Refresh token also expired, clear everything and redirect to login
        localStorage.removeItem("auth_token");
        sessionStorage.removeItem("auth_token");
        localStorage.removeItem("refresh_token");
        sessionStorage.removeItem("refresh_token");
        // window.location.href = "/login";
        return Promise.reject(error.response || error);
      }

      // If not already refreshing, start refresh process
      if (!isRefreshing) {
        isRefreshing = true;
        const refreshToken =
          localStorage.getItem("refresh_token") ||
          sessionStorage.getItem("refresh_token");

        if (!refreshToken) {
          // No refresh token available, clear and redirect
          localStorage.removeItem("auth_token");
          sessionStorage.removeItem("auth_token");
          localStorage.removeItem("refresh_token");
          sessionStorage.removeItem("refresh_token");
          processQueue(error, null);
          isRefreshing = false;
          // window.location.href = "/login";
          return Promise.reject(error.response || error);
        }

        try {
          // Call refresh token API (without using the interceptor to avoid loop)
          const response = await axios.post(
            `${instance.defaults.baseURL}/user/refresh`,
            { refreshToken },
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (response.data?.success && response.data?.data) {
            const { accessToken, refreshToken: newRefreshToken } = response.data.data;
            
            // Determine storage based on where refresh token was stored
            const rememberMe = !!localStorage.getItem("refresh_token");
            
            if (rememberMe) {
              localStorage.setItem("auth_token", accessToken);
              localStorage.setItem("refresh_token", newRefreshToken);
            } else {
              sessionStorage.setItem("auth_token", accessToken);
              sessionStorage.setItem("refresh_token", newRefreshToken);
            }

            // Update original request with new token
            originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
            
            // Process queued requests
            processQueue(null, accessToken);
            isRefreshing = false;

            // Retry original request
            return instance(originalRequest);
          } else {
            throw new Error("Refresh token failed");
          }
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect
          localStorage.removeItem("auth_token");
          sessionStorage.removeItem("auth_token");
          localStorage.removeItem("refresh_token");
          sessionStorage.removeItem("refresh_token");
          processQueue(refreshError, null);
          isRefreshing = false;
          // window.location.href = "/login";
          return Promise.reject(refreshError);
        }
      } else {
        // Already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return instance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
    }
    return Promise.reject(error.response || error);
  }
);

export default instance;
